import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Link,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material';
import {
  OpenInNew as ExternalIcon,
  WarningAmber as WarnIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  MARKETING_TOOLS,
  TOOL_CATEGORIES,
  RISK_LABELS,
  TWICSY_CASE_STUDY,
  type MarketingTool,
} from '../config/marketingTools';
import { useAuth } from '../contexts/AuthContext';

const ORDER: MarketingTool['category'][] = ['platform', 'freelance', 'engagement'];
/**
 * What happened when this was tried here, with the follower readings behind it.
 *
 * Deliberately placed with the paid-engagement category rather than in a footnote: a warning
 * that names a real account, a real date and a real consequence is the one people believe.
 * The bars are scaled to the peak, so the two bulk deliveries are visible as steps.
 */
const CaseStudy: React.FC = () => {
  const c = TWICSY_CASE_STUDY;
  const peak = Math.max(...c.timeline.map(t => t.followers));

  return (
    <Card sx={{ mb: 2, borderLeft: 3, borderLeftColor: 'error.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <HistoryIcon fontSize="small" color="error" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            What happened when we tried this
          </Typography>
          <Chip size="small" label={`${c.account} · ${c.date}`} variant="outlined" />
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>{c.summary}</Typography>

        {/* Follower readings, straight from audience_stats */}
        <Box sx={{ mb: 2 }}>
          {c.timeline.map(t => (
            <Box key={t.at} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" sx={{ width: 44, color: 'text.secondary', flexShrink: 0 }}>
                {t.at}
              </Typography>
              <Box
                sx={{
                  height: 14,
                  width: `${Math.max((t.followers / peak) * 100, 1)}%`,
                  bgcolor: t.followers > 1000 ? 'error.light' : 'success.light',
                  borderRadius: 0.5,
                  flexShrink: 0,
                  minWidth: 4,
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
                {t.followers.toLocaleString()}
              </Typography>
              {t.note && (
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                  {t.note}
                </Typography>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            Follower readings recorded hourly by this app — measurements, not recollection.
          </Typography>
        </Box>

        {/* Marked as a theory: the timeline above is measured, this is not */}
        {c.likelyCause && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              borderLeft: 3,
              borderLeftColor: 'text.disabled',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Why it went this way — a theory, not a confirmed cause
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {c.likelyCause}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>What it cost</Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: c.outcome ? 2 : 0 }}>
          {c.consequences.map((line, i) => (
            <Typography component="li" variant="body2" color="text.secondary" key={i} sx={{ mb: 0.5 }}>
              {line}
            </Typography>
          ))}
        </Box>

        {c.outcome ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>Outcome:</strong> {c.outcome}
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            The account review was still open when this was written. Whatever the result, the
            reach and analytics damage above is already done and is not undone by restoring the
            account.
          </Alert>
        )}

        {/* Kept here rather than in the guide: this is where someone looks when it happens */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          If an account gets locked over this
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
          {c.appeal.guidance.map((line, i) => (
            <Typography component="li" variant="body2" color="text.secondary" key={i} sx={{ mb: 0.5 }}>
              {line}
            </Typography>
          ))}
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Wording that is true, and works
        </Typography>
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            borderLeft: 3,
            borderLeftColor: 'info.main',
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {c.appeal.template}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Reviewers see invented victim stories constantly. Taking responsibility is rarer, reads
          as credible, and cannot be contradicted later by the seller's own records.
        </Typography>
      </CardContent>
    </Card>
  );
};


/**
 * Outside services worth knowing about.
 *
 * Entries that breach platform terms are listed rather than hidden — the team asked for them,
 * and knowing what a service is beats discovering it later. But they are labelled, and the
 * category carries a warning, because an unqualified link on an internal tool reads as an
 * endorsement from whoever built the page.
 */
export const MarketingTools: React.FC = () => {
  const { user } = useAuth();

  // Unlike the other admin pages, there is no API call here to return a 403 — the content
  // ships in the bundle — so the page refuses non-admins itself.
  if (user?.role !== 'admin') {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>Marketing Tools</Typography>
        <Alert severity="info">This section is only available to administrators.</Alert>
      </Box>
    );
  }

  return (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" component="h1">Marketing Tools</Typography>
      <Typography variant="body2" color="text.secondary">
        Outside services for social media work, and what each one actually does.
      </Typography>
    </Box>

    {ORDER.map(category => {
      const items = MARKETING_TOOLS.filter(r => r.category === category);
      if (!items.length) return null;
      const meta = TOOL_CATEGORIES[category];
      const isRisky = category === 'engagement';

      return (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h6">{meta.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {meta.blurb}
          </Typography>

          {/* Stated once for the category rather than repeated on every card */}
          {isRisky && (
            <Alert severity="warning" icon={<WarnIcon />} sx={{ mb: 2 }}>
              <AlertTitle>These breach the terms of every platform connected here</AlertTitle>
              Instagram, Facebook, LinkedIn and X all prohibit purchased followers, likes and
              views. Enforcement lands on the account that received them — which means the
              SolBid and HorizonXL pages publishing through this app, not the service that sold
              them. Bought engagement also arrives through the same API fields as real
              engagement, so Analytics can no longer tell you what is working.
            </Alert>
          )}

          {isRisky && <CaseStudy />}

          <Card>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {items.map((r, i) => (
                <Box
                  key={r.name}
                  sx={{
                    p: 2,
                    borderTop: i ? 1 : 0,
                    borderColor: 'divider',
                    borderLeft: 3,
                    borderLeftColor:
                      r.risk === 'terms-violation'
                        ? 'error.main'
                        : r.risk === 'caution'
                          ? 'warning.main'
                          : 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 0.5,
                    }}
                  >
                    <Link
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      sx={{
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {r.name}
                      <ExternalIcon sx={{ fontSize: 15 }} />
                    </Link>

                    {r.risk !== 'none' && (
                      <Chip
                        size="small"
                        label={RISK_LABELS[r.risk]}
                        color={r.risk === 'terms-violation' ? 'error' : 'warning'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}

                    {r.pricing && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {r.pricing}
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ mb: r.note ? 1 : 0 }}>
                    {r.description}
                  </Typography>

                  {r.note && (
                    <Typography variant="body2" color="text.secondary">
                      {r.note}
                    </Typography>
                  )}

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}
                  >
                    {r.url}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      );
    })}

    <Divider sx={{ mb: 2 }} />
    <Typography variant="caption" color="text.secondary">
      Listed for reference, not endorsed. Prices are indicative and change often — check the
      service. Nothing here is connected to ShoutVector or has access to your accounts; these are
      plain external links.
    </Typography>
  </Box>
  );
};
